/**
 * tm/portal-ui/ForwardToCeo.tsx — 팀장님 포털 업체 화면의 「대표님께 전달할 것」 (2026-10-28 지도사
 *   「상담 결과 보내면 항상 『이거 대표님께 전달드리면 되나요?』 문의를 받아 · FP님 페이지는 고객에게 보내면 안 되고
 *    고객에게 전달할 1. 상담리포트 파일 2. 필요서류 제출페이지 3. 고객 페이지만 전달하시도록」)
 *  있는 것만 한 줄씩 · 줄마다 「복사」 · 「대표님께 보낼 글 복사」(세 주소를 한 글로) · 맨 아래 「이 화면은 팀장님 전용」
 *  서버 GET /api/tm/fp-portal-data/:fpId/forward/:companyId(services/customerLinks — 대표님 진행 안내는 이 팀장님 몫 주소)
 *  2026-10-28 검토: 보기만 해서는 주소를 만들지 않는다 — 진행 안내 주소가 아직 없으면(client_can) 「복사」를 누를 때 POST 로 만든다 · 복사가 막힌 기기는 주소를 그대로 보여 줘 길게 눌러 복사
 *  2026-10-28 지도사 「이미 전달했는데 계속 상단에 보이는 것도 이상」 · 「얼핏 보면 이걸 보내지 말라는 듯 느껴져」
 *   — 복사하면 「보냄 10/28」로 남기고(서버 fp_forward_marks · 새 리포트 · 새 서류 요청이면 다시 「보낼 것」) · 다 보냈으면 맨 아래 한 줄로 내려간다(place)
 *   — 맨 아래 안내는 「위 주소만 보내 주세요」로(부정문이 「복사」 옆에 있어 보내지 말라는 뜻으로 읽혔다)
 */
import { useEffect, useState } from 'react';
import { Asset, Button, G, Group, ListHeader, ListRow } from './tds';
import { md } from './portalLogic';

type Kind = 'report' | 'collect' | 'client';
interface Links { report: string; report_date: string; collect: string; collect_left?: number; client: string; client_can?: boolean; sent?: Partial<Record<Kind, string>> }
const EMPTY: Links = { report: '', report_date: '', collect: '', client: '', sent: {} };

function copyText(t: string): Promise<boolean> {
  if (!navigator.clipboard || !navigator.clipboard.writeText) return Promise.resolve(false);
  return navigator.clipboard.writeText(t).then(() => true).catch(() => false);
}

/** 대표님께 보낼 글 — 팀장님이 그대로 붙여 보내는 글 */
export function ceoMessage(companyName: string, l: Pick<Links, 'report' | 'collect' | 'client'>): string {
  const rows = [['상담 리포트', l.report], ['필요서류 제출', l.collect], ['진행 안내', l.client]].filter((x) => x[1]);
  return [`안녕하세요 대표님, ${companyName} 임지도사 상담 관련 자료입니다.`, '', ...rows.map(([k, u]) => `· ${k}\n${u}`)].join('\n');
}

/* 위 · 아래 두 자리가 같은 자료를 쓰게 — 한 번만 불러오고 바뀌면 둘 다 다시 그림 */
const cache = new Map<string, Links>();
const loading = new Map<string, Promise<Links>>();
const EVT = 'fw-change';
function keyOf(fpId: string, companyId: string) { return `${fpId}|${companyId}`; }
function put(k: string, v: Links) { cache.set(k, v); try { window.dispatchEvent(new CustomEvent(EVT, { detail: k })); } catch { /* 옛 브라우저 */ } }
function load(base: string, k: string): Promise<Links> {
  const c = cache.get(k); if (c) return Promise.resolve(c);
  const p0 = loading.get(k); if (p0) return p0;
  const p = fetch(base).then((r) => r.json()).then((j: { success?: boolean; data?: Links }) => (j.success && j.data ? j.data : EMPTY)).catch(() => EMPTY)
    .then((v) => { loading.delete(k); put(k, v); return v; });
  loading.set(k, p); return p;
}

/**
 * place='top'  — 아직 보낼 것이 있을 때(업체 화면 맨 위)
 * place='bottom' — 다 보냈을 때(맨 아래 한 줄 · 「다시 보기」로 펼침)
 */
export default function ForwardToCeo({ fpId, companyId, companyName, m, toast, place = 'top' }: { fpId: string; companyId: string; companyName: string; m: string; toast: (msg: string) => void; place?: 'top' | 'bottom' }) {
  const k = keyOf(fpId, companyId);
  const base = `/api/tm/fp-portal-data/${encodeURIComponent(fpId)}/forward/${encodeURIComponent(companyId)}`;
  const [l, setL] = useState<Links | null>(cache.get(k) || null);
  const [shown, setShown] = useState<string>('');   // 복사가 안 되는 기기 — 이 줄 주소를 그대로 보여 줌
  const [open, setOpen] = useState(false);          // 다 보낸 뒤 「다시 보기」
  useEffect(() => {
    let alive = true;
    setL(cache.get(k) || null);
    void load(base, k).then((v) => { if (alive) setL(v); });
    const on = (e: Event) => { if ((e as CustomEvent).detail === k && alive) setL(cache.get(k) || null); };
    window.addEventListener(EVT, on);
    return () => { alive = false; window.removeEventListener(EVT, on); };
  }, [base, k]);
  if (!l) return null;
  /** 진행 안내 주소 — 없으면 이때 만든다(한 번) */
  const ensureClient = async (): Promise<string> => {
    if (l.client || !l.client_can) return l.client;
    try {
      const j = await (await fetch(`${base}/client`, { method: 'POST' })).json() as { success?: boolean; data?: { client: string }; error?: string };
      if (j.success && j.data) { put(k, { ...(cache.get(k) || l), client: j.data.client, client_can: false }); return j.data.client; }
      toast(j.error || '진행 안내 주소를 만들지 못했어요');
    } catch { toast('잠시 연결이 안 돼요'); }
    return '';
  };
  /** 보냄 표시 — 서버에 남기고 이 화면도 바로 */
  const mark = (kinds: Kind[]) => {
    void fetch(`${base}/copied`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kinds }) }).then((r) => r.json())
      .then((j: { success?: boolean; data?: { sent: Partial<Record<Kind, string>> } }) => { if (j.success && j.data) { const cur = cache.get(k) || l; put(k, { ...cur, sent: { ...(cur.sent || {}), ...j.data.sent } }); } })
      .catch(() => undefined);
  };
  const copy = (t: string, what: string, key: string, kinds: Kind[]) => { void copyText(t).then((ok) => { if (ok) { setShown(''); toast(`${what} 복사했어요 · 대표님께 그대로 보내 주세요`); mark(kinds); } else { setShown(key); toast('아래 주소를 길게 눌러 복사해 주세요'); } }); };
  const sent = l.sent || {};
  const rows: { k: Kind; icon: string; title: string; sub: string; url: string }[] = ([
    { k: 'report', icon: 'doc', title: '상담 리포트', sub: l.report_date ? `${md(l.report_date)} 상담 요약 PDF` : '상담 요약 PDF', url: l.report },
    { k: 'collect', icon: 'up', title: '필요서류 제출', sub: l.collect_left ? `대표님이 올릴 서류 ${l.collect_left}개` : '대표님이 직접 올리는 곳', url: l.collect },
    { k: 'client', icon: 'user', title: '대표님 진행 안내', sub: '대표님용 진행 화면', url: l.client || (l.client_can ? 'pending' : '') },
  ] as { k: Kind; icon: string; title: string; sub: string; url: string }[]).filter((x) => x.url);
  const allSent = rows.length > 0 && rows.every((x) => !!sent[x.k]);
  // 자리: 아직 보낼 것이 있으면 위 · 다 보냈으면 아래(보낼 것이 아예 없으면 아무 데도 안 보임 — 빈 상자로 자리 차지 ✕)
  if (rows.length === 0 || (place === 'top') === allSent) return null;
  const lastDay = rows.map((x) => sent[x.k] || '').sort().pop() || '';
  const list = (
    <>
      {rows.map((x) => (
        <div key={x.k}>
          <ListRow left={<Asset icon={x.icon} />} title={x.title} sub={sent[x.k] ? `${x.sub} · ${md(sent[x.k] as string)} 보냄` : x.sub} clip
            right={<Button kind={sent[x.k] ? 'gray' : 'weak'} h={44} w={104} full={false} onClick={() => { void (x.k === 'client' ? ensureClient() : Promise.resolve(x.url)).then((u) => { if (u) copy(u, x.title + ' 주소를', x.k, [x.k]); }); }}>{sent[x.k] ? '다시 복사' : '복사'}</Button>} />
          {shown === x.k && x.url !== 'pending' && <div style={{ padding: '0 20px 10px 76px', fontSize: 14, color: G.g700, wordBreak: 'break-all', userSelect: 'all' }}>{x.k === 'client' ? l.client : x.url}</div>}
        </div>
      ))}
      {rows.length > 1 && !allSent && <div style={{ padding: '4px 20px 8px' }}><Button kind="fill" h={48} onClick={() => { void ensureClient().then((c) => copy(ceoMessage(companyName, { ...l, client: c || l.client }), '대표님께 보낼 글을', 'all', rows.map((x) => x.k))); }}>대표님께 보낼 글 한 번에 복사</Button></div>}
      {shown === 'all' && <div style={{ margin: '0 20px 8px', padding: '10px 12px', borderRadius: 10, background: G.g100, fontSize: 14, color: G.g800, whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'all' }}>{ceoMessage(companyName, l)}</div>}
      <div style={{ padding: '6px 20px 10px', fontSize: 14, color: G.g600, lineHeight: 1.6, wordBreak: 'keep-all' }}>대표님께는 위 주소만 보내 주세요. 지금 보고 계신 화면은 팀장님 전용이에요.</div>
    </>
  );
  if (place === 'bottom') {
    return (
      <Group m={m}>
        <ListRow left={<Asset icon="check" />} title="대표님께 보낸 것" sub={`${rows.length}개 · ${md(lastDay)} 보냄`}
          right={<Button kind="gray" h={44} w={104} full={false} onClick={() => setOpen((v) => !v)}>{open ? '접기' : '다시 보기'}</Button>} />
        {open && list}
      </Group>
    );
  }
  return (
    <Group m={m}>
      <ListHeader title="대표님께 보낼 것" right="그대로 보내셔도 돼요" />
      {list}
    </Group>
  );
}
